import React, { useState, useRef, useEffect, useCallback } from 'react';
import { all_routes } from '../../../router/all_routes';
import { Link, useNavigate } from 'react-router-dom';
import CollapseHeader from "../../../../core/common/collapse-header";
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessAlert, showError } from '../../../../core/alert/sweetAlertConfig';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker, Polyline } from "@react-google-maps/api";
import type { Library } from "@googlemaps/js-api-loader";
import ModernPlaceAutocomplete from "../../../../core/common/ModernPlaceAutocomplete";
import { computeRoute, LatLngPoint } from "../../../../core/utils/routeService";
import GlassSelect from "../../../../core/common/glassSelect/GlassSelect";
 
const GOOGLE_MAPS_LIBRARIES: Library[] = ["places"];
 

 
interface LocationPayload {
  address: string;
  lat: string;
  lng: string;
  orderId: number;
  type: 'start' | 'stop' | 'return';
}
 
interface BookingForm {
  bookingType: string;
  priority: string;
  locations: LocationPayload[];
  reason: string;
  noOfPassengers: number;
  departureTime: string;
  arrivalTime: string;
  estimatedKms: number;
  totalWaitingTime: number; // In minutes
}
 
interface RecurringBookingForm {
  frequency: string;
  endDate: string;
}
 
interface SavedLocation {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}
 
interface GetLocationsResponse {
  locations: SavedLocation[];
  total: number;
}
 
interface StopWaitingTime {
  days: number;
  hours: number;
  minutes: number;
}
 
const center = { lat: 6.9271, lng: 79.8612 };
 
const NewBooking: React.FC = () => {
  const navigate = useNavigate();
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "AIzaSyBuEcklY7fN-33BDjLDlzRNxNU1uGvPfxk";
 
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapsApiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
 
  const [formData, setFormData] = useState<BookingForm>({
    bookingType: 'Planned',
    priority: 'P1',
    locations: [
      { address: '', lat: '', lng: '', orderId: 1, type: 'start' },
      { address: '', lat: '', lng: '', orderId: 2, type: 'stop' },
      { address: '', lat: '', lng: '', orderId: 3, type: 'return' },
    ],
    reason: '',
    noOfPassengers: 1,
    departureTime: '',
    arrivalTime: '',
    estimatedKms: 0,
    totalWaitingTime: 0,
  });
 
  const [recurringFormData, setRecurringFormData] = useState<RecurringBookingForm>({
    frequency: 'WEEKLY',
    endDate: '',
  });
 
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [routePath, setRoutePath] = useState<LatLngPoint[]>([]);
  const [vehicleBookingRequestId, setVehicleBookingRequestId] = useState<number | null>(null);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stopWaitingTimes, setStopWaitingTimes] = useState<{ [key: number]: StopWaitingTime }>({});
  const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
  const autocompleteRefs = useRef<(google.maps.places.Autocomplete | null)[]>([]);
  const mapRef = useRef<google.maps.Map | null>(null);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate(all_routes.login);
      return false;
    }
    return token;
  };

  const fetchSavedLocations = async () => {
    if (!checkToken()) return;
    try {
      const payload = { filterType: "", filterValue: "", date: "" };
      const response = await apiDefinitions.getLocations("1", payload);
      if (response.status === 200) {
        const data: GetLocationsResponse = response.data.data;
        setSavedLocations(data.locations || []);
      } else {
        showError(response.data?.statusDescription || "Failed to fetch locations");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError("Unauthorized: Invalid or expired token. Please log in again.");
        localStorage.removeItem("token");
        navigate(all_routes.login);
      } else {
        showError("Failed to fetch locations. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (isLoaded) {
      fetchSavedLocations();
    }
  }, [isLoaded]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRecurringInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setRecurringFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (index: number, field: string, value: string | number) => {
    setFormData(prev => {
      const newLocations = [...prev.locations];
      newLocations[index] = { ...newLocations[index], [field]: value };
      return { ...prev, locations: newLocations };
    });
  };

  const handleSavedLocationChange = (index: number, type: 'start' | 'stop' | 'return', name: string) => {
    const selectedLocation = savedLocations.find(loc => loc.name === name);

    if (!selectedLocation && name === "") {
      setFormData(prev => {
        const newLocations = [...prev.locations];
        newLocations[index] = { ...newLocations[index], address: '', lat: '', lng: '' };
        return { ...prev, locations: newLocations };
      });
      return;
    }
    if (!selectedLocation) return;

    let latNum = selectedLocation.lat;
    let lngNum = selectedLocation.lng;

    if ((latNum === undefined || lngNum === undefined) && selectedLocation.description) {
      const parts = selectedLocation.description.split(',').map(s => parseFloat(s.trim()));
      if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
        latNum = parts[0];
        lngNum = parts[1];
      }
    }

    if (Number.isNaN(latNum) || Number.isNaN(lngNum) || latNum === undefined || lngNum === undefined) {
      showError("Invalid coordinates for selected location");
      return;
    }

    setFormData(prev => {
      const newLocations = [...prev.locations];
      newLocations[index] = {
        ...newLocations[index],
        address: selectedLocation.name,
        lat: latNum.toString(),
        lng: lngNum.toString(),
      };
      return { ...prev, locations: newLocations };
    });
  };
 
  const handleWaitingTimeChange = (index: number, field: keyof StopWaitingTime, value: number) => {
    setStopWaitingTimes(prev => {
      const newWaitingTimes = {
        ...prev,
        [index]: {
          ...prev[index],
          [field]: value >= 0 ? value : 0,
        },
      };
      const total = Object.values(newWaitingTimes).reduce((sum, time) => {
        const days = time?.days || 0;
        const hours = time?.hours || 0;
        const minutes = time?.minutes || 0;
        return sum + (days * 24 * 60) + (hours * 60) + minutes;
      }, 0);
      setFormData(prevForm => ({ ...prevForm, totalWaitingTime: total }));
      return newWaitingTimes;
    });
  };
 
  const addStopLocation = () => {
    setFormData(prev => ({
      ...prev,
      locations: [
        ...prev.locations.slice(0, -1),
        {
          address: '',
          lat: '',
          lng: '',
          orderId: prev.locations.length,
          type: 'stop',
        },
        prev.locations[prev.locations.length - 1],
      ],
    }));
  };
 
  const removeStopLocation = (index: number) => {
    setFormData(prev => {
      const newLocations = prev.locations.filter((_, i) => i !== index).map((loc, i) => ({
        ...loc,
        orderId: i + 1,
      }));
      return { ...prev, locations: newLocations };
    });
    setStopWaitingTimes(prev => {
      const newWaitingTimes = { ...prev };
      delete newWaitingTimes[index];
      const total = Object.values(newWaitingTimes).reduce((sum, time) => {
        const days = time?.days || 0;
        const hours = time?.hours || 0;
        const minutes = time?.minutes || 0;
        return sum + (days * 24 * 60) + (hours * 60) + minutes;
      }, 0);
      setFormData(prevForm => ({ ...prevForm, totalWaitingTime: total }));
      return newWaitingTimes;
    });
  };
 
  const onPlaceChanged = (index: number) => {
    const autocomplete = autocompleteRefs.current[index];
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place && place.geometry && place.geometry.location) {
        const address = place.formatted_address || '';
        const lat = place.geometry.location.lat().toString();
        const lng = place.geometry.location.lng().toString();
        setFormData(prev => {
          const newLocations = [...prev.locations];
          newLocations[index] = { ...newLocations[index], address, lat, lng };
          return { ...prev, locations: newLocations };
        });
      } else {
        showError(`Invalid location selected for ${formData.locations[index].type}. Please choose a valid address.`);
        setFormData(prev => {
          const newLocations = [...prev.locations];
          newLocations[index] = { ...newLocations[index], address: '', lat: '', lng: '' };
          return { ...prev, locations: newLocations };
        });
      }
    }
  };
 
  const routeDurationSecondsRef = useRef<number>(0);

  const calculateRoute = useCallback(async () => {
    if (!isLoaded) {
      return;
    }

    const origin = formData.locations.find((loc) => loc.type === 'start');
    const destination = formData.locations.find((loc) => loc.type === 'return');

    const hasStart = origin?.lat && origin?.lng && !isNaN(parseFloat(origin.lat)) && !isNaN(parseFloat(origin.lng));
    const hasReturn = destination?.lat && destination?.lng && !isNaN(parseFloat(destination.lat)) && !isNaN(parseFloat(destination.lng));

    if (!hasStart || !hasReturn) {
      setRoutePath([]);
      setDirections(null);
      setFormData((prev) => (prev.estimatedKms !== 0 ? { ...prev, estimatedKms: 0 } : prev));
      return;
    }

    const stops = formData.locations.filter((loc) => loc.type === 'stop');
    const waypoints: LatLngPoint[] = stops
      .filter((loc) => loc.lat && loc.lng && !isNaN(parseFloat(loc.lat)) && !isNaN(parseFloat(loc.lng)))
      .map((loc) => ({
        lat: parseFloat(loc.lat),
        lng: parseFloat(loc.lng),
      }));

    try {
      const result = await computeRoute(
        { lat: parseFloat(origin!.lat), lng: parseFloat(origin!.lng) },
        { lat: parseFloat(destination!.lat), lng: parseFloat(destination!.lng) },
        waypoints,
        googleMapsApiKey
      );

      setRoutePath(result.path);
      routeDurationSecondsRef.current = result.durationSeconds;

      const totalWaitingSeconds = (formData.totalWaitingTime || 0) * 60;

      let computedArrivalTime = formData.arrivalTime;
      if (formData.departureTime) {
        const departureDate = new Date(formData.departureTime);
        if (!isNaN(departureDate.getTime())) {
          const arrivalDate = new Date(
            departureDate.getTime() + (result.durationSeconds + totalWaitingSeconds) * 1000
          );
          const pad = (num: number) => num.toString().padStart(2, '0');
          computedArrivalTime = `${arrivalDate.getFullYear()}-${pad(
            arrivalDate.getMonth() + 1
          )}-${pad(arrivalDate.getDate())}T${pad(arrivalDate.getHours())}:${pad(
            arrivalDate.getMinutes()
          )}`;
        }
      }

      setFormData((prev) => ({
        ...prev,
        estimatedKms: result.estimatedKms,
        ...(computedArrivalTime ? { arrivalTime: computedArrivalTime } : {}),
      }));

      if (mapRef.current && result.path.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        result.path.forEach((point) => bounds.extend(point));
        mapRef.current.fitBounds(bounds);
      }
    } catch (error) {
      console.warn('Failed to calculate route directions:', error);
    }
  }, [isLoaded, formData.locations, formData.departureTime, formData.totalWaitingTime, formData.arrivalTime, googleMapsApiKey]);

  // Automated trigger whenever locations, departure time, or waiting times change
  const locationsKey = formData.locations
    .map((l) => `${l.type}:${l.lat},${l.lng}`)
    .join('|');

  useEffect(() => {
    if (!isLoaded) return;
    const timer = setTimeout(() => {
      calculateRoute();
    }, 400);
    return () => clearTimeout(timer);
  }, [isLoaded, locationsKey, formData.departureTime, formData.totalWaitingTime, calculateRoute]);

  // Sync Arrival Time whenever Departure Time or Waiting Times change
  useEffect(() => {
    if (!formData.departureTime || routeDurationSecondsRef.current <= 0) return;
    const departureDate = new Date(formData.departureTime);
    if (isNaN(departureDate.getTime())) return;

    const totalWaitingSeconds = (formData.totalWaitingTime || 0) * 60;
    const arrivalDate = new Date(
      departureDate.getTime() + (routeDurationSecondsRef.current + totalWaitingSeconds) * 1000
    );
    const pad = (num: number) => num.toString().padStart(2, '0');
    const computedArrival = `${arrivalDate.getFullYear()}-${pad(
      arrivalDate.getMonth() + 1
    )}-${pad(arrivalDate.getDate())}T${pad(arrivalDate.getHours())}:${pad(
      arrivalDate.getMinutes()
    )}`;

    setFormData((prev) => (prev.arrivalTime !== computedArrival ? { ...prev, arrivalTime: computedArrival } : prev));
  }, [formData.departureTime, formData.totalWaitingTime]);
  const validateTimeDifference = () => {
    const departure = new Date(formData.departureTime).getTime();
    const arrival = new Date(formData.arrivalTime).getTime();
    const diffHours = (arrival - departure) / (1000 * 60 * 60);
 
    const priorityHours: { [key: string]: number } = {
      P1: 12,
      P2: 24,
      P3: 48,
    };
 
    const maxHours = priorityHours[formData.priority];
    if (diffHours > maxHours) {
      showError(`The time difference for ${formData.priority} priority should not exceed ${maxHours} hours.`);
      return false;
    }
    return true;
  };
 
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
 
    if (!validateTimeDifference()) return;
 
    if (formData.locations.some(loc => !loc.address || !loc.lat || !loc.lng)) {
      showError('Please fill all location details.');
      return;
    }
 
    if (formData.totalWaitingTime < 0) {
      showError('Total waiting time cannot be negative.');
      return;
    }
 
    const now = new Date();
    const departure = new Date(formData.departureTime);
    const maxFutureDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    if (departure > maxFutureDate) {
      showError('Departure time cannot be more than 30 days in the future.');
      return;
    }
 
    const token = checkToken();
    if (!token) return;
 
    setIsSubmitting(true);
    try {
      const payload = {
        reason: formData.reason,
        noOfPassengers: formData.noOfPassengers,
        departureTime: new Date(formData.departureTime).getTime().toString(),
        waitingTime: `${(formData.totalWaitingTime / 60).toFixed(1)} hours`,
        arrivalTime: new Date(formData.arrivalTime).getTime().toString(),
        estimatedKms: Number(formData.estimatedKms.toFixed(2)),
        bookingType: formData.bookingType,
        priority: formData.priority,
        locations: formData.locations.map(({ address, lat, lng, orderId }) => ({
          id: orderId,
          address,
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        })),
      };
 
      const bookingResponse = await apiDefinitions.addVehicleBooking(payload);
      if (bookingResponse.status === 200 || bookingResponse.status === 201) {
        showSuccessAlert("Vehicle booking created successfully");
        if (formData.bookingType === 'Recurring' && checkPermission('ROLE_ADD_RECURRING_VEHICLE_BOOKING_REQUESTS')) {
          const latestIdResponse = await apiDefinitions.getLatestVehicleBookingId();
          const latestId = latestIdResponse.data?.data;
          if (latestId) {
            setVehicleBookingRequestId(latestId);
            setShowRecurringForm(true);
          } else {
            showError('No booking ID returned from the server.');
          }
        } else {
          navigate(all_routes.booking);
        }
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else if (error.response?.status === 400) {
        showError(`Bad Request: ${error.response?.data?.statusDescription || 'Please check the input data format.'}`);
      } else if (error.response?.status === 500) {
        showError(`Server Error: ${error.response?.data?.statusDescription || 'An unexpected error occurred on the server.'}`);
      } else {
        showError('Failed to create vehicle booking. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleRecurringSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleBookingRequestId) {
      showError('No vehicle booking ID available.');
      return;
    }
 
    const token = checkToken();
    if (!token) return;
 
    try {
      const recurringPayload = {
        vehicleBookingRequestId,
        frequency: recurringFormData.frequency,
        endDate: new Date(recurringFormData.endDate).getTime().toString(),
      };
 
      const response = await apiDefinitions.addRecurringBooking(recurringPayload);
      if (response.status === 200 || response.status === 201) {
        showSuccessAlert();
        resetForm();
        setShowRecurringForm(false);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate(all_routes.login);
      } else {
        showError('Failed to create recurring booking. Please try again.');
      }
    }
  };
 
  const resetForm = () => {
    setFormData({
      bookingType: 'Planned',
      priority: 'P1',
      locations: [
        { address: '', lat: '', lng: '', orderId: 1, type: 'start' },
        { address: '', lat: '', lng: '', orderId: 2, type: 'stop' },
        { address: '', lat: '', lng: '', orderId: 3, type: 'return' },
      ],
      reason: '',
      noOfPassengers: 1,
      departureTime: '',
      arrivalTime: '',
      estimatedKms: 0,
      totalWaitingTime: 0,
    });
    setRecurringFormData({
      frequency: 'WEEKLY',
      endDate: '',
    });
    setStopWaitingTimes({});
    setDirections(null);
    setVehicleBookingRequestId(null);
  };
 
  if (!googleMapsApiKey) {
    return <div>Error: Google Maps API key not configured</div>;
  }
 
  if (loadError) {
    return <div>Error loading Google Maps: {loadError.message}</div>;
  }
 
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    <i className="ti ti-car" style={{ color: "var(--rm-primary)" }} />
                    <span>New Vehicle Booking</span>
                  </h4>
                  <div className="text-muted small mt-1">Vehicle Management &bull; Booking &bull; New Request</div>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header justify-content-between">
            <div className="card-title">New Vehicle Booking</div>
          </div>
          <div className="card-body">
            {isLoaded ? (
              <>
                {!showRecurringForm ? (
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Booking Type</label>
                          <GlassSelect
                            name="bookingType"
                            value={formData.bookingType}
                            onChange={(val) => setFormData(prev => ({ ...prev, bookingType: val }))}
                            options={[
                              { value: 'Planned', label: 'Planned' },
                              ...(checkPermission('ROLE_ADD_RECURRING_VEHICLE_BOOKING_REQUESTS')
                                ? [{ value: 'Recurring', label: 'Recurring' }]
                                : []),
                              { value: 'Sudden', label: 'Sudden' },
                              { value: 'Outstation', label: 'Outstation' },
                            ]}
                            placeholder="Select Booking Type"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Priority</label>
                          <GlassSelect
                            name="priority"
                            value={formData.priority}
                            onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                            options={[
                              { value: 'P1', label: 'P1 (Max 12 hours)' },
                              { value: 'P2', label: 'P2 (Max 24 hours)' },
                              { value: 'P3', label: 'P3 (Max 48 hours)' },
                            ]}
                            placeholder="Select Priority"
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Reason</label>
                          <textarea
                            name="reason"
                            className="form-control"
                            placeholder="Reason for booking"
                            value={formData.reason}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Number of Passengers</label>
                          <input
                            type="number"
                            name="noOfPassengers"
                            className="form-control"
                            placeholder="Number of Passengers"
                            value={formData.noOfPassengers}
                            onChange={handleInputChange}
                            min="1"
                          />
                        </div>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <label className="form-label mb-0">Departure Time</label>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-primary py-0 px-2"
                              style={{ fontSize: '0.75rem' }}
                              onClick={() => {
                                const now = new Date();
                                const pad = (num: number) => num.toString().padStart(2, '0');
                                const formatted = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                                setFormData(prev => ({ ...prev, departureTime: formatted }));
                              }}
                            >
                              <i className="ti ti-clock me-1" />Use Current Time
                            </button>
                          </div>
                          <input
                            type="datetime-local"
                            name="departureTime"
                            className="form-control"
                            value={formData.departureTime}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Arrival Time</label>
                          <input
                            type="datetime-local"
                            name="arrivalTime"
                            className="form-control"
                            value={formData.arrivalTime}
                            onChange={handleInputChange}
                          />
                          {formData.arrivalTime && (
                            <small className="form-text text-muted">
                              Arrival time is auto-calculated based on route, departure time, and waiting times. You can manually adjust it if needed.
                            </small>
                          )}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Estimated KMs</label>
                          <div className="form-control-plaintext" style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                            {formData.estimatedKms > 0 ? `${formData.estimatedKms} Km` : <span className="text-muted">0</span>}
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Total Waiting Time</label>
                          <div className="form-control-plaintext" style={{ fontWeight: 600, fontSize: "1.1rem" }}>
                            {formData.totalWaitingTime > 0
                              ? `${Math.floor(formData.totalWaitingTime / (24 * 60))} days, ${Math.floor((formData.totalWaitingTime % (24 * 60)) / 60)} hours, ${formData.totalWaitingTime % 60} minutes`
                              : <span className="text-muted">0</span>}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Locations</label>
                          {formData.locations.map((location, index) => {
                            const selectedSavedName = savedLocations.some((l) => l.name === formData.locations[index].address)
                              ? formData.locations[index].address
                              : "";
                            return (
                              <div className="mb-3" key={index}>
                                <label className="form-label">
                                  {location.type.charAt(0).toUpperCase() + location.type.slice(1)} Location
                                </label>
                                <div className="row g-2 align-items-center mb-2">
                                  <div className={location.type === 'stop' ? "col-md-6 col-12" : "col-md-6 col-12"}>
                                    <ModernPlaceAutocomplete
                                      placeholder={`Search ${location.type} address...`}
                                      value={formData.locations[index].address}
                                      onChange={(val) => handleLocationChange(index, 'address', val)}
                                      onPlaceSelect={(address, lat, lng) => {
                                        setFormData(prev => {
                                          const newLocations = [...prev.locations];
                                          newLocations[index] = { ...newLocations[index], address, lat, lng };
                                          return { ...prev, locations: newLocations };
                                        });
                                      }}
                                    />
                                  </div>
                                  <div className={location.type === 'stop' ? "col-md-4 col-8" : "col-md-6 col-12"}>
                                    <GlassSelect
                                      value={selectedSavedName}
                                      onChange={(val) => handleSavedLocationChange(index, location.type, val)}
                                      options={[
                                        { value: '', label: 'Choose saved location...' },
                                        ...savedLocations.map((loc) => ({
                                          value: loc.name,
                                          label: loc.name,
                                        })),
                                      ]}
                                      placeholder="Choose saved location..."
                                      allowClear
                                      searchable={savedLocations.length > 5}
                                    />
                                  </div>
                                  {location.type === 'stop' && (
                                    <div className="col-md-2 col-4 text-end">
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm w-100 d-inline-flex align-items-center justify-content-center gap-1"
                                        onClick={() => removeStopLocation(index)}
                                        disabled={formData.locations.filter((loc) => loc.type === 'stop').length <= 1}
                                        title="Remove Stop"
                                      >
                                        <i className="ti ti-trash" />
                                        <span>Remove</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {location.type === 'stop' && (
                                  <div className="row g-2">
                                    <div className="col-4">
                                      <label className="form-label">Waiting Time (Days)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Days"
                                        value={stopWaitingTimes[index]?.days || 0}
                                        onChange={(e) => handleWaitingTimeChange(index, 'days', Number(e.target.value))}
                                        min="0"
                                      />
                                    </div>
                                    <div className="col-4">
                                      <label className="form-label">Waiting Time (Hours)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Hours"
                                        value={stopWaitingTimes[index]?.hours || 0}
                                        onChange={(e) => handleWaitingTimeChange(index, 'hours', Number(e.target.value))}
                                        min="0"
                                      />
                                    </div>
                                    <div className="col-4">
                                      <label className="form-label">Waiting Time (Minutes)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Minutes"
                                        value={stopWaitingTimes[index]?.minutes || 0}
                                        onChange={(e) => handleWaitingTimeChange(index, 'minutes', Number(e.target.value))}
                                        min="0"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="btn btn-outline-primary d-inline-flex align-items-center gap-2 mt-2"
                            onClick={addStopLocation}
                          >
                            <i className="ti ti-plus" />
                            <span>Add Stop Location</span>
                          </button>
                        </div>
                        <div className="mb-3">
                          <GoogleMap
                            mapContainerStyle={{ height: "300px", width: "100%" }}
                            center={center}
                            zoom={12}
                            onLoad={(map) => {
                              mapRef.current = map;
                            }}
                          >
                            {formData.locations.map((location, index) => (
                              location.lat && location.lng && (
                                <Marker
                                  key={index}
                                  position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                                  title={location.type.charAt(0).toUpperCase() + location.type.slice(1)}
                                />
                              )
                            ))}
                            {routePath.length > 0 && (
                              <Polyline
                                path={routePath}
                                options={{
                                  strokeColor: '#2563EB',
                                  strokeOpacity: 0.9,
                                  strokeWeight: 5,
                                }}
                              />
                            )}
                            {directions && (
                              <DirectionsRenderer
                                directions={directions}
                                options={{
                                  polylineOptions: {
                                    strokeColor: '#2563EB',
                                    strokeOpacity: 0.8,
                                    strokeWeight: 5,
                                  },
                                }}
                              />
                            )}
                          </GoogleMap>
                        </div>
                      </div>
                      <div className="col-md-12">
                        <button
                          type="submit"
                          className="btn btn-primary d-inline-flex align-items-center gap-2 px-4 py-2"
                          disabled={isSubmitting}
                        >
                          <i className="ti ti-check" />
                          <span>
                            {isSubmitting
                              ? 'Submitting...'
                              : formData.bookingType === 'Recurring' && checkPermission('ROLE_ADD_RECURRING_VEHICLE_BOOKING_REQUESTS')
                              ? 'Next'
                              : 'Submit Booking'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleRecurringSubmit}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Frequency</label>
                          <GlassSelect
                            name="frequency"
                            value={recurringFormData.frequency}
                            onChange={(val) => setRecurringFormData(prev => ({ ...prev, frequency: val }))}
                            options={[
                              { value: 'DAILY', label: 'Daily' },
                              { value: 'WEEKLY', label: 'Weekly' },
                              { value: 'MONTHLY', label: 'Monthly' },
                            ]}
                            placeholder="Select Frequency"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">End Date</label>
                          <input
                            type="date"
                            name="endDate"
                            className="form-control"
                            value={recurringFormData.endDate}
                            onChange={handleRecurringInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Recurring Booking'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary ms-2"
                          onClick={() => setShowRecurringForm(false)}
                        >
                          Back
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <div>Loading Google Maps...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default NewBooking;
 